import { useStoreProductArticles, useStoreStock } from '@gozoki/api'
import { MenuItem, Paper, Select } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import SimpleDataGrid from '~/components/containers/SimpleDataGrid'
import { HeaderPicker } from '~/components/routes/stores/HeaderPicker'
import { StoreDetailsLayout } from '~/components/routes/stores/StoreDetailsLayout'
import {
  articleStateFilter,
  articlesColDef,
  toArticleRow,
  type FilterArticleState,
} from '~/components/routes/stores/stocks/stockGrids'
import { STORE_TAB_PERMISSIONS } from '~/components/routes/stores/tabs'
import { withRoutePermissions } from '~/utils/rbac'
import { DataGridOverrides } from '~/utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/stores/$storeId/stocks/product/$productId')({
  // Même écran que l'onglet Stocks, resserré sur un produit : même droit de lecture.
  staticData: {
    permission: STORE_TAB_PERMISSIONS.stocks,
  },
  component: StoreProductArticlesPage,
  params: {
    parse: z.object({ productId: z.coerce.number(), storeId: z.coerce.number() }).parse,
  },
})
withRoutePermissions(Route)

function StoreProductArticlesPage() {
  const navigate = Route.useNavigate()
  const { storeId, productId } = Route.useParams()

  const [articleState, setArticleState] = useState<FilterArticleState>('all')

  const stock = useStoreStock(storeId)
  const articles = useStoreProductArticles(storeId, productId)

  const productOptions = useMemo(
    () => (stock.data?.rows ?? []).map((row) => ({ label: row.label, value: row.id })),
    [stock.data],
  )

  const articlesRows = useMemo(() => {
    if (articles.data === undefined) {
      return []
    }
    return articles.data.filter((a) => articleStateFilter(a, articleState)).map(toArticleRow)
  }, [articles.data, articleState])

  return (
    <StoreDetailsLayout
      storeId={storeId}
      tab="stocks"
      titlePicker={
        <HeaderPicker
          label="Produit"
          options={productOptions}
          value={productId}
          loading={stock.isLoading}
          onChange={(target) =>
            navigate({
              to: '/stores/$storeId/stocks/product/$productId',
              params: { productId: target, storeId },
            })
          }
        />
      }
      // Le produit consulté n'existe pas forcément dans la boutique choisie : on revient au stock.
      onStoreChange={(target) =>
        navigate({ to: '/stores/$storeId/stocks', params: { storeId: target } })
      }
      onBack={() => navigate({ to: '/stores/$storeId/stocks', params: { storeId } })}
    >
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <Select
            value={articleState}
            onChange={(e) => setArticleState(e.target.value as FilterArticleState)}
          >
            <MenuItem value="all">Tout</MenuItem>
            <MenuItem value="antigaspi">Anti-gaspi</MenuItem>
            <MenuItem value="expired">Expiré</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
          </Select>
          <div style={{ flexGrow: 1 }} />
        </div>
        <SimpleDataGrid
          columns={articlesColDef}
          rows={articlesRows}
          getRowId={(row) => row.id}
          loading={articles.isLoading}
          checkboxSelection={false}
          sx={DataGridOverrides.wrapHeaderNames}
        />
      </Paper>
    </StoreDetailsLayout>
  )
}
